import React, { useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { TextBoxComponent, NumericTextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { CheckBoxComponent } from '@syncfusion/ej2-react-buttons';
import { type CrudFormProps } from '../../components/CrudPage';
import { MaterialControllerService, type MaterialDto, ResourceTypeControllerService, type ResourceTypeDto } from '../../api';
import { FormDialog } from '../../components/FormDialog';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { resolveValidationMessage } from '../../utils/validationMessages';

type MaterialWithId = MaterialDto & { id: string };

// Material type options
export type MaterialType = 'PRODUCT' | 'RAW_MATERIAL' | 'INTERMEDIATE_PRODUCT';

// Create schema function to use translations
const createMaterialSchema = (t: any) => {
  const requiredMessage = t('validation.required') || 'Dieses Feld ist erforderlich';
  
  return z.object({
    name: z.string().min(1, requiredMessage),
    description: z.string().optional().nullable(),
    baseUnit: z.string().min(1, requiredMessage),
    baseUnitType: z.string().optional(),
    global: z.boolean().optional().default(false),
    materialType: z.string().optional(),
    isAlias: z.boolean().optional(),
    aliasForProductUuid: z.string().optional().nullable(),
    // Product-specific fields
    articleNumber: z.string().optional(),
    initialWeighInGrams: z.union([z.number(), z.null()]).optional(),
    scrapRate: z.union([z.number(), z.null()]).optional(),
    minDoughQuantityInKg: z.union([z.number(), z.null()]).optional(),
    maxDoughQuantityInKg: z.union([z.number(), z.null()]).optional(),
    // New product-specific fields
    defaultOvenTypeUuid: z.string().optional().nullable(),
    longRunning: z.boolean().optional(),
    longTermOffsetDays: z.union([z.number().int().min(1).max(31), z.null()]).optional(),
    piecesPerOven: z.union([z.number().min(0, 'validation.minValueZero'), z.null()]).optional(),
    startTemperature: z.union([z.number().min(0, 'validation.minValueZero'), z.null()]).optional(),
    endTemperature: z.union([z.number().min(0, 'validation.minValueZero'), z.null()]).optional(),
    doughBatchMethod: z.string().optional(),
    ovenBatchDetermination: z.string().optional(),
    minOvenUtilizationPercent: z.union([z.number(), z.null()]).optional(),
    maxProductionLeadTimeHours: z.union([z.number().int().min(0, 'validation.minValueZero'), z.null()]).optional(),
    requireContiguousCampaignBatches: z.boolean().optional()
  }).superRefine((data, ctx) => {
    if (data.materialType === 'PRODUCT') {
      if (!data.ovenBatchDetermination) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: requiredMessage,
          path: ['ovenBatchDetermination']
        });
      }
      if (!data.doughBatchMethod) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: requiredMessage,
          path: ['doughBatchMethod']
        });
      }
      // The default oven, pieces per oven (>0) and the temperatures are mandatory fields for products
      if (!data.defaultOvenTypeUuid || String(data.defaultOvenTypeUuid).trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: requiredMessage,
          path: ['defaultOvenTypeUuid']
        });
      }
      if (data.piecesPerOven == null || data.piecesPerOven <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('validation.minValueGreaterThanZero') || 'Muss größer als 0 sein',
          path: ['piecesPerOven']
        });
      }
      if (data.startTemperature == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: requiredMessage,
          path: ['startTemperature']
        });
      }
      if (data.endTemperature == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: requiredMessage,
          path: ['endTemperature']
        });
      }
    }

    if (data.materialType === 'PRODUCT' || data.materialType === 'INTERMEDIATE_PRODUCT') {
      if (data.longRunning && (data.longTermOffsetDays == null || data.longTermOffsetDays < 1 || data.longTermOffsetDays > 31)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('product.longTermOffsetDaysValidation') || 'Versatz (Tage) muss zwischen 1 und dem konfigurierten Maximum liegen.',
          path: ['longTermOffsetDays']
        });
      }
      if (data.isAlias && !data.aliasForProductUuid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: requiredMessage,
          path: ['aliasForProductUuid']
        });
      }
      if (data.initialWeighInGrams == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: requiredMessage,
          path: ['initialWeighInGrams']
        });
      }
      if (data.minDoughQuantityInKg == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: requiredMessage,
          path: ['minDoughQuantityInKg']
        });
      }
      if (data.maxDoughQuantityInKg == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: requiredMessage,
          path: ['maxDoughQuantityInKg']
        });
      }
    }

    if (data.ovenBatchDetermination === 'OVEN_UTILIZATION') {
      if (data.minOvenUtilizationPercent == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: requiredMessage,
          path: ['minOvenUtilizationPercent']
        });
      } else if (data.minOvenUtilizationPercent <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'validation.minValueGreaterThanZero',
          path: ['minOvenUtilizationPercent']
        });
      } else {
        const scaled = Math.round(data.minOvenUtilizationPercent * 10) / 10;
        if (Math.abs(scaled - data.minOvenUtilizationPercent) > 1e-6) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'validation.oneDecimalPlaceRequired',
            path: ['minOvenUtilizationPercent']
          });
        }
      }
    }
  });
};

// Form data type that matches the schema input (pre-parse)
type MaterialFormData = z.input<ReturnType<typeof createMaterialSchema>>;

const MaterialFormDialog: React.FC<CrudFormProps<MaterialWithId> & { materialType?: MaterialType }> = ({
  value,
  onClose,
  onSave,
  materialType: propMaterialType
}) => {
  const { t } = useTranslation(['common']);
  const [ovenTypes, setOvenTypes] = useState<ResourceTypeDto[]>([]);
  const [loadingOvenTypes, setLoadingOvenTypes] = useState(false);
  const [aliasProducts, setAliasProducts] = useState<MaterialDto[]>([]);
  const [loadingAliasProducts, setLoadingAliasProducts] = useState(false);
  
  const materialSchema = React.useMemo(() => {
    return createMaterialSchema(t);
  }, [t]);

  // Determine material type: use prop if provided, otherwise use value
  const materialType = propMaterialType || value?.materialType;

  // Load oven types when component mounts or when materialType is PRODUCT or INTERMEDIATE_PRODUCT
  useEffect(() => {
    const loadOvenTypes = async () => {
      if (materialType === 'PRODUCT' || materialType === 'INTERMEDIATE_PRODUCT') {
        setLoadingOvenTypes(true);
        try {
          const resourceTypes = await ResourceTypeControllerService.findByCategory('OVEN');
          setOvenTypes(resourceTypes);
        } catch (error) {
          console.error('Error loading oven types:', error);
          setOvenTypes([]);
        } finally {
          setLoadingOvenTypes(false);
        }
      }
    };
    loadOvenTypes();
  }, [materialType]);

  useEffect(() => {
    const loadAliasProducts = async () => {
      if (materialType === 'PRODUCT') {
        setLoadingAliasProducts(true);
        try {
          const materials = await MaterialControllerService.findAll();
          const products = materials.filter(material => material.materialType === 'PRODUCT' && !material.isAlias);
          setAliasProducts(products);
        } catch (error) {
          console.error('Error loading alias products:', error);
          setAliasProducts([]);
        } finally {
          setLoadingAliasProducts(false);
        }
      }
    };
    loadAliasProducts();
  }, [materialType]);


  const ovenBatchDeterminationOptions = [
    { text: t('product.batchDeterminationValues.fullOven'), value: 'FULL_OVEN' },
    { text: t('product.batchDeterminationValues.fullStove'), value: 'FULL_STOVE' },
    { text: t('product.batchDeterminationValues.fullCauldron'), value: 'FULL_CAULDRON' },
    { text: t('product.batchDeterminationValues.ovenUtilization'), value: 'OVEN_UTILIZATION' }
    // MANUAL was removed - it is equivalent to OVEN_UTILIZATION with an individual quantity (phase 1)
  ];

  const doughBatchMethodOptions = [
    { text: t('product.doughBatchMethodValues.even'), value: 'EVEN' },
    { text: t('product.doughBatchMethodValues.full'), value: 'FULL' }
  ];

  // Base units based on unit type
  const baseUnitsByType = {
    PIECE: [
      { text: t('form.baseUnit.piece'), value: 'COUNT' }
    ],
    MASS: [
      { text: t('form.baseUnit.gram'), value: 'GRAM' },
      { text: t('form.baseUnit.kilogram'), value: 'KILOGRAM' },
      { text: t('form.baseUnit.milligram'), value: 'MILLIGRAM' },
      { text: t('form.baseUnit.ton'), value: 'TON' }
    ],
    VOLUME: [
      { text: t('form.baseUnit.liter'), value: 'LITER' },
      { text: t('form.baseUnit.milliliter'), value: 'MILLILITER' },
      { text: t('form.baseUnit.cubicMeter'), value: 'CUBIC_METER' }
    ],
    LENGTH: [
      { text: t('form.baseUnit.meter'), value: 'METER' },
      { text: t('form.baseUnit.centimeter'), value: 'CENTIMETER' },
      { text: t('form.baseUnit.millimeter'), value: 'MILLIMETER' },
      { text: t('form.baseUnit.kilometer'), value: 'KILOMETER' }
    ],
    AREA: [
      { text: t('form.baseUnit.squareMeter'), value: 'SQUARE_METER' },
      { text: t('form.baseUnit.squareCentimeter'), value: 'SQUARE_CENTIMETER' },
      { text: t('form.baseUnit.squareMillimeter'), value: 'SQUARE_MILLIMETER' }
    ]
  };

  // Preprocess the value to convert null to empty strings
  // For products, baseUnit is always 'COUNT' and baseUnitType is always 'PIECE'
  const processedValue = value ? {
    ...value,
    name: value.name ?? '',
    baseUnit: (materialType === 'PRODUCT' || materialType === 'INTERMEDIATE_PRODUCT') ? 'COUNT' : (value.baseUnit ?? ''),
    baseUnitType: (materialType === 'PRODUCT' || materialType === 'INTERMEDIATE_PRODUCT') ? 'PIECE' : (value.baseUnitType ?? ''),
    description: value.description ?? null,
    articleNumber: value.articleNumber ?? '',
    initialWeighInGrams: value.initialWeighInGrams ?? undefined,
    scrapRate: value.scrapRate ?? 0, // Default to 0 for scrapRate
    minDoughQuantityInKg: value.minDoughQuantityInKg ?? undefined,
    maxDoughQuantityInKg: value.maxDoughQuantityInKg ?? undefined,
    longRunning: value.longRunning ?? false,
    longTermOffsetDays: value.longTermOffsetDays ?? undefined,
    piecesPerOven: value.piecesPerOven ?? undefined,
    startTemperature: value.startTemperature ?? undefined,
    endTemperature: value.endTemperature ?? undefined,
    doughBatchMethod: value.doughBatchMethod ?? (materialType === 'PRODUCT' ? 'EVEN' : undefined),
    ovenBatchDetermination: (() => {
      const raw = value.ovenBatchDetermination ?? value.batchDetermination ?? (materialType === 'PRODUCT' ? 'FULL_OVEN' : undefined);
      return raw === 'MANUAL' ? 'OVEN_UTILIZATION' : raw;
    })(),
    minOvenUtilizationPercent: value.minOvenUtilizationPercent ?? undefined,
    maxProductionLeadTimeHours: value.maxProductionLeadTimeHours ?? undefined,
    requireContiguousCampaignBatches: value.requireContiguousCampaignBatches ?? false,
    isAlias: value.isAlias ?? false,
    aliasForProductUuid: value.aliasForProductUuid ?? undefined,
    materialType: value.materialType || propMaterialType
  } : {
    name: '',
    baseUnit: (materialType === 'PRODUCT' || materialType === 'INTERMEDIATE_PRODUCT') ? 'COUNT' : '',
    baseUnitType: (materialType === 'PRODUCT' || materialType === 'INTERMEDIATE_PRODUCT') ? 'PIECE' : '',
    description: null,
    global: false,
    articleNumber: '',
    initialWeighInGrams: undefined,
    scrapRate: 0, // Default to 0 for scrapRate
    minDoughQuantityInKg: undefined,
    maxDoughQuantityInKg: undefined,
    longRunning: false,
    longTermOffsetDays: undefined,
    piecesPerOven: undefined,
    startTemperature: undefined,
    endTemperature: undefined,
    doughBatchMethod: materialType === 'PRODUCT' ? 'EVEN' : undefined,
    ovenBatchDetermination: materialType === 'PRODUCT' ? 'FULL_OVEN' : undefined,
    minOvenUtilizationPercent: undefined,
    maxProductionLeadTimeHours: undefined,
    requireContiguousCampaignBatches: false,
    isAlias: false,
    aliasForProductUuid: undefined,
    materialType: propMaterialType
  };

  return (
    <FormDialog<MaterialFormData>
      value={processedValue as MaterialFormData | null}
      onClose={onClose}
      onSave={(data: MaterialFormData) => {
        // Convert form data back to MaterialWithId format
        // For products, baseUnit is always 'COUNT' and baseUnitType is always 'PIECE'
        const materialData: MaterialWithId = {
          ...data,
          id: value?.id || '',
          uuid: value?.uuid, // Include uuid for updates
          tenantId: value?.tenantId,
          tenantName: value?.tenantName,
          description: data.description || undefined, // Convert null to undefined
          defaultOvenTypeUuid: data.defaultOvenTypeUuid || undefined, // Convert null to undefined
          minOvenUtilizationPercent: data.ovenBatchDetermination === 'OVEN_UTILIZATION'
            ? (data.minOvenUtilizationPercent ?? undefined)
            : undefined,
          isAlias: data.isAlias ?? false,
          aliasForProductUuid: data.aliasForProductUuid ?? undefined,
          doughBatchMethod: data.doughBatchMethod ?? undefined,
          ovenBatchDetermination: data.ovenBatchDetermination ?? undefined,
          minDoughQuantityInKg: data.minDoughQuantityInKg ?? undefined,
          maxDoughQuantityInKg: data.maxDoughQuantityInKg ?? undefined,
          initialWeighInGrams: data.initialWeighInGrams ?? undefined,
          scrapRate: data.scrapRate ?? undefined,
          piecesPerOven: data.piecesPerOven ?? undefined,
          startTemperature: data.startTemperature ?? undefined,
          endTemperature: data.endTemperature ?? undefined,
          maxProductionLeadTimeHours: data.maxProductionLeadTimeHours ?? undefined,
          requireContiguousCampaignBatches: data.requireContiguousCampaignBatches ?? false,
          baseUnit: (materialType === 'PRODUCT' || materialType === 'INTERMEDIATE_PRODUCT') ? 'COUNT' : data.baseUnit,
          baseUnitType: (materialType === 'PRODUCT' || materialType === 'INTERMEDIATE_PRODUCT') ? 'PIECE' : data.baseUnitType,
          longTermOffsetDays: data.longTermOffsetDays ?? undefined
        };
        onSave(materialData);
      }}
      titleKey="dialogs.material"
      width="900px"
      resolver={zodResolver(materialSchema)}
    >
      {({ control, formState: { errors }, setValue, watch }) => {
        // Get all available base units (flatten all unit types)
        const allBaseUnits = [
          ...baseUnitsByType.PIECE,
          ...baseUnitsByType.MASS,
          ...baseUnitsByType.VOLUME,
          ...baseUnitsByType.LENGTH,
          ...baseUnitsByType.AREA
        ];

        const baseUnitValueToUnitType: Record<string, 'PIECE' | 'MASS' | 'VOLUME' | 'LENGTH' | 'AREA'> = {};
        (Object.entries(baseUnitsByType) as [keyof typeof baseUnitsByType, { value: string }[]][]).forEach(
          ([unitType, units]) => {
            units.forEach((u) => {
              baseUnitValueToUnitType[u.value] = unitType;
            });
          }
        );

        const selectedOvenTypeUuid = watch('defaultOvenTypeUuid');
        const selectedOvenType = ovenTypes.find(type => type.uuid === selectedOvenTypeUuid);
        const isStoneOven = selectedOvenType?.ovenType === 'STONE_OVEN';
        const ovenBatchDetermination = watch('ovenBatchDetermination');
        const isAlias = watch('isAlias') || false;
        const availableOvenBatchDeterminations = isStoneOven
          ? ovenBatchDeterminationOptions
          : ovenBatchDeterminationOptions.filter(option => option.value !== 'FULL_STOVE');


        return (
        <>
          {/* 1. Name and Article Number in a row */}
          <div className="mb-4 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.name')} *</label>
              <Controller
                name="name"
                control={control}
                rules={{ required: t('validation.required') }}
                render={({ field }) => (
                  <TextBoxComponent
                    {...field}
                    placeholder={t('form.name') + ' *'}
                    floatLabelType="Never"
                    cssClass={errors.name ? 'e-error' : ''}
                  />
                )}
              />
              {errors.name && (
                <div className="text-red-500 text-sm mt-1">
                  {resolveValidationMessage(t, errors.name)}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('material.articleNumber')}</label>
              <Controller
                name="articleNumber"
                control={control}
                render={({ field }) => (
                  <TextBoxComponent
                    {...field}
                    value={field.value ?? ''}
                    placeholder={t('material.articleNumber')}
                    floatLabelType="Never"
                    cssClass={errors.articleNumber ? 'e-error' : ''}
                  />
                )}
              />
              {errors.articleNumber && (
                <div className="text-red-500 text-sm mt-1">
                  {resolveValidationMessage(t, errors.articleNumber)}
                </div>
              )}
            </div>
          </div>

          {/* 2. Description */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.description')}</label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextBoxComponent
                  {...field}
                  value={field.value ?? ''}
                  placeholder={t('form.description')}
                  floatLabelType="Never"
                  cssClass={errors.description ? 'e-error' : ''}
                />
              )}
            />
            {errors.description && (
              <div className="text-red-500 text-sm mt-1">
                {resolveValidationMessage(t, errors.description)}
              </div>
            )}
          </div>

          {/* 2b+3. Products: one row with the alias (left, dynamic width) | long-running + offset (right); below that the base unit on the right */}
          {materialType === 'PRODUCT' && (
            <div className="mb-4 grid grid-cols-2 gap-4">
              {/* Row 1 left: alias checkbox + dropdown (the dropdown fills the rest of the column) */}
              <div className="flex items-center gap-3 flex-nowrap min-w-0">
                <Controller
                  name="isAlias"
                  control={control}
                  render={({ field }) => (
                    <CheckBoxComponent
                      label={t('product.isAlias')}
                      checked={field.value || false}
                      change={(e: { checked?: boolean }) => {
                        field.onChange(e.checked);
                        if (!e.checked) {
                          setValue('aliasForProductUuid', undefined);
                        }
                      }}
                    />
                  )}
                />
                {watch('isAlias') && (
                  <>
                    <div className="min-w-0 flex-1">
                      <Controller
                        name="aliasForProductUuid"
                        control={control}
                        render={({ field }) => (
                          <DropDownListComponent
                            dataSource={aliasProducts.filter(product => product.uuid !== value?.uuid)}
                            fields={{ text: 'name', value: 'uuid' }}
                            placeholder={t('product.aliasForProduct')}
                            floatLabelType="Never"
                            cssClass={errors.aliasForProductUuid ? 'e-error' : ''}
                            value={field.value}
                            enabled={!loadingAliasProducts}
                            locale="de-DE"
                            style={{ width: '100%' }}
                            change={(e: { value?: string }) => {
                              const currentArticleNumber = watch('articleNumber');
                              field.onChange(e.value || undefined);
                              const selectedProduct = aliasProducts.find(product => product.uuid === e.value);
                              if (selectedProduct) {
                                setValue('minDoughQuantityInKg', selectedProduct.minDoughQuantityInKg ?? undefined);
                                setValue('maxDoughQuantityInKg', selectedProduct.maxDoughQuantityInKg ?? undefined);
                                setValue('initialWeighInGrams', selectedProduct.initialWeighInGrams ?? undefined);
                                setValue('scrapRate', selectedProduct.scrapRate ?? undefined);
                                setValue('defaultOvenTypeUuid', selectedProduct.defaultOvenTypeUuid ?? undefined);
                                setValue('longRunning', selectedProduct.longRunning ?? false);
                                setValue('longTermOffsetDays', selectedProduct.longTermOffsetDays ?? undefined);
                                setValue('piecesPerOven', selectedProduct.piecesPerOven ?? undefined);
                                setValue('startTemperature', selectedProduct.startTemperature ?? undefined);
                                setValue('endTemperature', selectedProduct.endTemperature ?? undefined);
                                setValue('doughBatchMethod', selectedProduct.doughBatchMethod ?? undefined);
                                setValue('ovenBatchDetermination', selectedProduct.ovenBatchDetermination ?? selectedProduct.batchDetermination ?? undefined);
                                setValue('minOvenUtilizationPercent', selectedProduct.minOvenUtilizationPercent ?? undefined);
                                setValue('maxProductionLeadTimeHours', selectedProduct.maxProductionLeadTimeHours ?? undefined);
                                setValue('requireContiguousCampaignBatches', selectedProduct.requireContiguousCampaignBatches ?? false);
                              }
                              if (currentArticleNumber) {
                                setValue('articleNumber', currentArticleNumber);
                              }
                            }}
                          />
                        )}
                      />
                    </div>
                    {errors.aliasForProductUuid && (
                      <span className="text-red-500 text-sm shrink-0">{resolveValidationMessage(t, errors.aliasForProductUuid)}</span>
                    )}
                  </>
                )}
              </div>
              {/* Row 1 right: long-running + offset (days), the offset field fills the rest of the column */}
              <div className="flex items-center gap-3 flex-wrap min-w-0">
                <Controller
                  name="longRunning"
                  control={control}
                  render={({ field }) => (
                    <CheckBoxComponent
                      label={t('product.longRunning')}
                      checked={field.value || false}
                      disabled={isAlias}
                      change={(e: { checked?: boolean }) => {
                        field.onChange(e.checked);
                      }}
                    />
                  )}
                />
                {watch('longRunning') && (
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <label className="text-sm text-gray-700 whitespace-nowrap">{t('product.longTermOffsetDays')}</label>
                    <div className="min-w-0 flex-1">
                      <Controller
                        name="longTermOffsetDays"
                        control={control}
                        render={({ field }) => (
                          <NumericTextBoxComponent
                            placeholder="1–31"
                            format="n0"
                            min={1}
                            max={31}
                            value={field.value ?? undefined}
                            change={(e: { value?: number | string | null }) => field.onChange(e.value ?? undefined)}
                            enabled={!isAlias}
                            cssClass={errors.longTermOffsetDays ? 'e-error' : ''}
                            style={{ width: '100%' }}
                          />
                        )}
                      />
                    </div>
                    {errors.longTermOffsetDays && (
                      <span className="text-red-500 text-sm shrink-0">{resolveValidationMessage(t, errors.longTermOffsetDays)}</span>
                    )}
                  </div>
                )}
              </div>
              {/* Row 2: base unit on the left, max. production lead time on the right */}
              <div className="flex flex-col">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.baseUnit')} *</label>
                <DropDownListComponent
                  dataSource={allBaseUnits}
                  fields={{ text: 'text', value: 'value' }}
                  placeholder={t('form.baseUnit') + ' *'}
                  floatLabelType="Never"
                  cssClass={errors.baseUnit ? 'e-error' : ''}
                  value={watch('baseUnit') || 'COUNT'}
                  enabled={false}
                  locale="de-DE"
                />
                {errors.baseUnit && (
                  <div className="text-red-500 text-sm mt-1">
                    {resolveValidationMessage(t, errors.baseUnit)}
                  </div>
                )}
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-1 mb-1">
                  <label className="block text-sm font-medium text-gray-700">{t('product.maxProductionLeadTimeDays')}</label>
                  <span
                    className="text-blue-500 cursor-help shrink-0"
                    title={t('product.maxProductionLeadTimeDaysHelp')}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </span>
                </div>
                <Controller
                  name="maxProductionLeadTimeHours"
                  control={control}
                  render={({ field }) => (
                    <NumericTextBoxComponent
                      value={field.value != null ? field.value / 24 : undefined}
                      change={(e: { value?: number | string | null }) =>
                        field.onChange(e.value != null ? Math.round(Number(e.value) * 24) : undefined)
                      }
                      placeholder={t('product.maxProductionLeadTimeDaysPlaceholder')}
                      floatLabelType="Never"
                      format="n0"
                      min={0}
                      enabled={!isAlias}
                      cssClass={errors.maxProductionLeadTimeHours ? 'e-error' : ''}
                    />
                  )}
                />
                {errors.maxProductionLeadTimeHours && (
                  <div className="text-red-500 text-sm mt-1">
                    {resolveValidationMessage(t, errors.maxProductionLeadTimeHours)}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 3. Intermediate product only: long-running on the left (offset dynamic), base unit on the right */}
          {materialType === 'INTERMEDIATE_PRODUCT' && (
            <div className="mb-4 grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3 flex-wrap min-w-0">
                <Controller
                  name="longRunning"
                  control={control}
                  render={({ field }) => (
                    <CheckBoxComponent
                      label={t('product.longRunning')}
                      checked={field.value || false}
                      disabled={isAlias}
                      change={(e: { checked?: boolean }) => {
                        field.onChange(e.checked);
                      }}
                    />
                  )}
                />
                {watch('longRunning') && (
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <label className="text-sm text-gray-700 whitespace-nowrap">{t('product.longTermOffsetDays')}</label>
                    <div className="min-w-0 flex-1">
                      <Controller
                        name="longTermOffsetDays"
                        control={control}
                        render={({ field }) => (
                          <NumericTextBoxComponent
                            placeholder="1–31"
                            format="n0"
                            min={1}
                            max={31}
                            value={field.value ?? undefined}
                            change={(e: { value?: number | string | null }) => field.onChange(e.value ?? undefined)}
                            enabled={!isAlias}
                            cssClass={errors.longTermOffsetDays ? 'e-error' : ''}
                            style={{ width: '100%' }}
                          />
                        )}
                      />
                    </div>
                    {errors.longTermOffsetDays && (
                      <span className="text-red-500 text-sm shrink-0">{resolveValidationMessage(t, errors.longTermOffsetDays)}</span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex flex-col">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.baseUnit')} *</label>
                <DropDownListComponent
                  dataSource={allBaseUnits}
                  fields={{ text: 'text', value: 'value' }}
                  placeholder={t('form.baseUnit') + ' *'}
                  floatLabelType="Never"
                  cssClass={errors.baseUnit ? 'e-error' : ''}
                  value={watch('baseUnit') || 'COUNT'}
                  enabled={false}
                  locale="de-DE"
                />
                {errors.baseUnit && (
                  <div className="text-red-500 text-sm mt-1">
                    {resolveValidationMessage(t, errors.baseUnit)}
                  </div>
                )}
              </div>
            </div>
          )}

          {materialType !== 'PRODUCT' && materialType !== 'INTERMEDIATE_PRODUCT' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.baseUnit')} *</label>
              <Controller
                name="baseUnit"
                control={control}
                render={({ field }) => (
                  <DropDownListComponent
                    dataSource={allBaseUnits}
                    fields={{ text: 'text', value: 'value' }}
                    placeholder={t('form.baseUnit') + ' *'}
                    floatLabelType="Never"
                    cssClass={errors.baseUnit ? 'e-error' : ''}
                    value={field.value}
                    change={(e: { value?: string }) => {
                      const v = e.value ?? '';
                      field.onChange(v);
                      setValue('baseUnitType', baseUnitValueToUnitType[v] ?? '');
                    }}
                    locale="de-DE"
                  />
                )}
              />
              {errors.baseUnit && (
                <div className="text-red-500 text-sm mt-1">
                  {resolveValidationMessage(t, errors.baseUnit)}
                </div>
              )}
            </div>
          )}

          {/* 4. Initial Weigh In and Scrap Rate in a row */}
          <div className="mb-4 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('material.initialWeighIn')} *</label>
              <Controller
                name="initialWeighInGrams"
                control={control}
                render={({ field }) => (
                  <NumericTextBoxComponent
                    value={field.value ?? undefined}
                    change={(e: { value?: number | string | null }) => field.onChange(e.value)}
                    placeholder={t('material.initialWeighIn') + ' *'}
                    floatLabelType="Never"
                    format="n0"
                    enabled={!isAlias}
                    cssClass={errors.initialWeighInGrams ? 'e-error' : ''}
                  />
                )}
              />
              {errors.initialWeighInGrams && (
                <div className="text-red-500 text-sm mt-1">
                  {resolveValidationMessage(t, errors.initialWeighInGrams)}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('material.scrapRate')}</label>
              <Controller
                name="scrapRate"
                control={control}
                render={({ field }) => (
                  <NumericTextBoxComponent
                    value={field.value ?? undefined}
                    change={(e: { value?: number | string | null }) => field.onChange(e.value)}
                    placeholder={t('material.scrapRate')}
                    floatLabelType="Never"
                    format="p2"
                    min={0}
                    max={0.9999}
                    step={0.0001}
                    enabled={!isAlias}
                    cssClass={errors.scrapRate ? 'e-error' : ''}
                  />
                )}
              />
              {errors.scrapRate && (
                <div className="text-red-500 text-sm mt-1">
                  {resolveValidationMessage(t, errors.scrapRate)}
                </div>
              )}
            </div>
          </div>

          {/* 5. Min and Max Dough Quantity in a row */}
          <div className="mb-4 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('material.minDoughQuantity')} *</label>
              <Controller
                name="minDoughQuantityInKg"
                control={control}
                render={({ field }) => (
                  <NumericTextBoxComponent
                    value={field.value ?? undefined}
                    change={(e: { value?: number | string | null }) => field.onChange(e.value)}
                    placeholder={t('material.minDoughQuantity') + ' *'}
                    floatLabelType="Never"
                    format="n0"
                    enabled={!isAlias}
                    cssClass={errors.minDoughQuantityInKg ? 'e-error' : ''}
                  />
                )}
              />
              {errors.minDoughQuantityInKg && (
                <div className="text-red-500 text-sm mt-1">
                  {resolveValidationMessage(t, errors.minDoughQuantityInKg)}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('material.maxDoughQuantity')} *</label>
              <Controller
                name="maxDoughQuantityInKg"
                control={control}
                render={({ field }) => (
                  <NumericTextBoxComponent
                    value={field.value ?? undefined}
                    change={(e: { value?: number | string | null }) => field.onChange(e.value)}
                    placeholder={t('material.maxDoughQuantity') + ' *'}
                    floatLabelType="Never"
                    format="n0"
                    enabled={!isAlias}
                    cssClass={errors.maxDoughQuantityInKg ? 'e-error' : ''}
                  />
                )}
              />
              {errors.maxDoughQuantityInKg && (
                <div className="text-red-500 text-sm mt-1">
                  {resolveValidationMessage(t, errors.maxDoughQuantityInKg)}
                </div>
              )}
            </div>
          </div>

          {/* New Product-specific fields - only show for Products and Intermediate Products */}
          {(materialType === 'PRODUCT' || materialType === 'INTERMEDIATE_PRODUCT') && (
            <>
              {/* 6. Default Oven Type and Pieces Per Oven in a row */}
              <div className="mb-4 grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('product.defaultOvenType')}{materialType === 'PRODUCT' ? ' *' : ''}</label>
                  <Controller
                    name="defaultOvenTypeUuid"
                    control={control}
                    render={({ field }) => (
                      <DropDownListComponent
                        dataSource={ovenTypes}
                        fields={{ text: 'name', value: 'uuid' }}
                        placeholder={materialType === 'PRODUCT' ? t('product.defaultOvenType') + ' *' : t('product.defaultOvenType')}
                        floatLabelType="Never"
                        cssClass={errors.defaultOvenTypeUuid ? 'e-error' : ''}
                        value={field.value}
                        enabled={!loadingOvenTypes && !isAlias}
                        locale="de-DE"
                        change={(e: { value?: string | null }) => {
                          field.onChange(e.value || null);
                          const selectedType = ovenTypes.find(type => type.uuid === e.value);
                          const isSelectedStoneOven = selectedType?.ovenType === 'STONE_OVEN';
                          if (!isSelectedStoneOven && watch('ovenBatchDetermination') === 'FULL_STOVE') {
                            setValue('ovenBatchDetermination', 'FULL_OVEN');
                          }
                        }}
                      />
                    )}
                  />
                  {errors.defaultOvenTypeUuid && (
                    <div className="text-red-500 text-sm mt-1">
                      {resolveValidationMessage(t, errors.defaultOvenTypeUuid)}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('product.piecesPerOven')}{materialType === 'PRODUCT' ? ' *' : ''}</label>
                  <Controller
                    name="piecesPerOven"
                    control={control}
                    render={({ field }) => (
                      <NumericTextBoxComponent
                        value={field.value ?? undefined}
                        change={(e: { value?: number | string | null }) => field.onChange(e.value)}
                        placeholder={materialType === 'PRODUCT' ? t('product.piecesPerOven') + ' *' : t('product.piecesPerOven')}
                        floatLabelType="Never"
                        format="n0"
                        min={materialType === 'PRODUCT' ? 1 : 0}
                        enabled={!isAlias}
                        cssClass={errors.piecesPerOven ? 'e-error' : ''}
                      />
                    )}
                  />
                  {errors.piecesPerOven && (
                    <div className="text-red-500 text-sm mt-1">
                      {resolveValidationMessage(t, errors.piecesPerOven)}
                    </div>
                  )}
                </div>
              </div>

              {/* 7. Start and End Temperature in a row */}
              <div className="mb-4 grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('product.startTemperature')}{materialType === 'PRODUCT' ? ' *' : ''}</label>
                  <Controller
                    name="startTemperature"
                    control={control}
                    render={({ field }) => (
                      <NumericTextBoxComponent
                        value={field.value ?? undefined}
                        change={(e: { value?: number | string | null }) => field.onChange(e.value)}
                        placeholder={materialType === 'PRODUCT' ? t('product.startTemperature') + ' *' : t('product.startTemperature')}
                        floatLabelType="Never"
                        format="n0"
                        min={0}
                        enabled={!isAlias}
                        cssClass={errors.startTemperature ? 'e-error' : ''}
                      />
                    )}
                  />
                  {errors.startTemperature && (
                    <div className="text-red-500 text-sm mt-1">
                      {resolveValidationMessage(t, errors.startTemperature)}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('product.endTemperature')}{materialType === 'PRODUCT' ? ' *' : ''}</label>
                  <Controller
                    name="endTemperature"
                    control={control}
                    render={({ field }) => (
                      <NumericTextBoxComponent
                        value={field.value ?? undefined}
                        change={(e: { value?: number | string | null }) => field.onChange(e.value)}
                        placeholder={materialType === 'PRODUCT' ? t('product.endTemperature') + ' *' : t('product.endTemperature')}
                        floatLabelType="Never"
                        format="n0"
                        min={0}
                        enabled={!isAlias}
                        cssClass={errors.endTemperature ? 'e-error' : ''}
                      />
                    )}
                  />
                  {errors.endTemperature && (
                    <div className="text-red-500 text-sm mt-1">
                      {resolveValidationMessage(t, errors.endTemperature)}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Batch determination fields - only show for Products */}
          {materialType === 'PRODUCT' && (
            <>
              <div className="mb-4 grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('product.doughBatchMethod')} *</label>
                  <Controller
                    name="doughBatchMethod"
                    control={control}
                    render={({ field }) => (
                      <DropDownListComponent
                        dataSource={doughBatchMethodOptions}
                        fields={{ text: 'text', value: 'value' }}
                        placeholder={t('product.doughBatchMethod') + ' *'}
                        floatLabelType="Never"
                        cssClass={errors.doughBatchMethod ? 'e-error' : ''}
                        value={field.value}
                        enabled={!isAlias}
                        change={(e: { value?: string }) => field.onChange(e.value || undefined)}
                      />
                    )}
                  />
                  {errors.doughBatchMethod && (
                    <div className="text-red-500 text-sm mt-1">
                      {resolveValidationMessage(t, errors.doughBatchMethod)}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('product.ovenBatchDetermination')} *</label>
                  <Controller
                    name="ovenBatchDetermination"
                    control={control}
                    render={({ field }) => (
                      <DropDownListComponent
                        dataSource={availableOvenBatchDeterminations}
                        fields={{ text: 'text', value: 'value' }}
                        placeholder={t('product.ovenBatchDetermination') + ' *'}
                        floatLabelType="Never"
                        cssClass={errors.ovenBatchDetermination ? 'e-error' : ''}
                        value={field.value}
                        enabled={!isAlias}
                        change={(e: { value?: string }) => field.onChange(e.value || undefined)}
                      />
                    )}
                  />
                  {errors.ovenBatchDetermination && (
                    <div className="text-red-500 text-sm mt-1">
                      {resolveValidationMessage(t, errors.ovenBatchDetermination)}
                    </div>
                  )}
                </div>
              </div>
              <div className="mb-4 grid grid-cols-2 gap-4">
                {ovenBatchDetermination === 'OVEN_UTILIZATION' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('product.minOvenUtilizationPercent')} *</label>
                    <Controller
                      name="minOvenUtilizationPercent"
                      control={control}
                      render={({ field }) => (
                        <NumericTextBoxComponent
                          value={field.value ?? undefined}
                          change={(e: { value?: number | string | null }) => field.onChange(e.value)}
                          placeholder={t('product.minOvenUtilizationPercent') + ' *'}
                          floatLabelType="Never"
                          format="n1"
                          step={0.1}
                          min={0}
                          enabled={!isAlias}
                          cssClass={errors.minOvenUtilizationPercent ? 'e-error' : ''}
                        />
                      )}
                    />
                    {errors.minOvenUtilizationPercent && (
                      <div className="text-red-500 text-sm mt-1">
                        {resolveValidationMessage(t, errors.minOvenUtilizationPercent)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {materialType === 'PRODUCT' && (
            <div className="mb-4">
              <Controller
                name="requireContiguousCampaignBatches"
                control={control}
                render={({ field }) => (
                  <CheckBoxComponent
                    label={t('product.requireContiguousCampaignBatches', 'Aufarbeitung: Alle Chargen einer Kampagne am Stück')}
                    checked={field.value || false}
                    disabled={isAlias}
                    change={(e: { checked?: boolean }) => field.onChange(e.checked)}
                  />
                )}
              />
            </div>
          )}

          {/* Global Flag Checkbox */}
          <div className="mb-4">
            <Controller
              name="global"
              control={control}
              render={({ field }) => (
                <CheckBoxComponent
                  label={t('form.global')}
                  checked={field.value || false}
                  change={(e: { checked?: boolean }) => field.onChange(e.checked)}
                />
              )}
            />
          </div>
        </>
        );
      }}
    </FormDialog>
  );
};

export default MaterialFormDialog;
